<template>
  <div>
    <v-alert v-if="!usuarioLogado.adminWeb" type="warning" outlined dense class="mb-4">
      Acesso restrito: faça login como administrador para consultar devolutivas.
    </v-alert>
    <v-container v-else fluid class="devolutiva-container">
      <v-card class="pa-5 mb-6" elevation="2">
        <v-card-title class="font-weight-bold">Gerar Devolutiva</v-card-title>
        <v-row align="center">
          <v-col cols="12" md="2">
            <v-text-field v-model.number="ano" label="Ano" type="number" outlined dense hide-details></v-text-field>
          </v-col>
          <v-col cols="12" md="5">
            <v-select
              v-model="idInstrumento"
              :items="instrumentos"
              item-text="descricao"
              item-value="id"
              label="Instrumento"
              outlined
              dense
              hide-details
            ></v-select>
          </v-col>
          <v-col cols="12" md="5">
            <v-select
              v-model="idInstituicao"
              :items="escolas"
              item-text="descricao"
              item-value="pkescola"
              label="Instituição"
              outlined
              dense
              hide-details
            ></v-select>
          </v-col>
        </v-row>
        <v-row align="center">
          <v-col cols="12" md="9">
            <div class="caption text--secondary">Questionários</div>
            <v-card outlined class="questionarios-lista">
              <div v-if="!questionarios.length" class="text--secondary caption pa-3">
                Selecione um instrumento para listar os questionários.
              </div>
              <v-checkbox
                v-for="item in questionarios"
                :key="item.id"
                v-model="idsQuestionario"
                :value="item.id"
                :label="rotuloQuestionario(item)"
                dense
                hide-details
                class="mx-3 my-1"
              ></v-checkbox>
            </v-card>
          </v-col>
          <v-col cols="12" md="3">
            <v-btn
              color="primary"
              block
              :loading="gerando"
              :disabled="!idInstituicao || !idsQuestionario.length"
              @click="gerarDevolutiva"
            >
              <v-icon left>mdi-file-pdf-box</v-icon>
              Gerar
            </v-btn>
          </v-col>
        </v-row>
      </v-card>

      <v-card elevation="2" class="pa-4">
        <v-card-title class="font-weight-bold d-flex align-center px-0">
          Devolutivas Geradas
          <v-spacer />
          <div style="max-width: 140px" class="mr-3">
            <v-text-field v-model.number="anoConsulta" label="Ano" type="number" outlined dense hide-details></v-text-field>
          </div>
          <v-btn color="primary" small :loading="buscandoLista" @click="buscarDevolutivas">
            <v-icon left small>mdi-magnify</v-icon>
            Buscar
          </v-btn>
        </v-card-title>

        <v-alert v-if="pastaSuportada" dense text :type="pastaNome ? 'success' : 'info'" class="mb-4">
          <div class="d-flex align-center flex-wrap">
            <span class="mr-3">
              {{ pastaNome ? `Pasta padrão: ${pastaNome}` : "Nenhuma pasta padrão selecionada." }}
            </span>
            <v-btn small outlined color="primary" @click="selecionarPasta">
              <v-icon left small>mdi-folder-open</v-icon>
              {{ pastaNome ? "Trocar pasta" : "Selecionar pasta" }}
            </v-btn>
          </div>
        </v-alert>
        <v-alert v-else dense text type="warning" class="mb-4">
          Seu navegador não permite fixar uma pasta padrão (use Chrome ou Edge). "Baixar todos" ainda funciona, salvando na
          pasta de downloads padrão do navegador.
        </v-alert>

        <template v-if="instituicoes.length">
          <v-expansion-panels>
            <v-expansion-panel v-for="inst in instituicoes" :key="inst">
              <v-expansion-panel-header>
                <div class="d-flex align-center justify-space-between mr-4">
                  <span class="d-flex align-center">
                    <span>{{ inst }}</span>
                    <v-icon class="ml-2" small>mdi-domain</v-icon>
                  </span>
                  <v-btn
                    small
                    outlined
                    color="primary"
                    :loading="baixandoTodos[inst]"
                    :disabled="!(arquivosPorInstituicao[inst] || []).length"
                    @click.stop="baixarTodos(inst)"
                  >
                    <v-icon left small>mdi-download-multiple</v-icon>
                    Baixar todos
                  </v-btn>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div v-if="!(arquivosPorInstituicao[inst] || []).length" class="text--secondary">
                  Nenhum PDF encontrado nesta pasta.
                </div>
                <v-list v-else dense>
                  <v-list-item v-for="arquivo in arquivosPorInstituicao[inst]" :key="arquivo">
                    <v-list-item-icon>
                      <v-icon color="red darken-2">mdi-file-pdf-box</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>{{ arquivo }}</v-list-item-title>
                    <v-list-item-action class="d-flex flex-row align-center">
                      <v-btn icon :loading="baixando[`${inst}/${arquivo}`]" title="Baixar" @click="baixarArquivo(inst, arquivo)">
                        <v-icon>mdi-download</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        color="error"
                        :loading="excluindo[`${inst}/${arquivo}`]"
                        title="Excluir"
                        @click="excluirArquivo(inst, arquivo)"
                      >
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                    </v-list-item-action>
                  </v-list-item>
                </v-list>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </template>

        <v-card-text v-else-if="buscandoLista" class="text-center pa-8">
          <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
        </v-card-text>
        <v-card-text v-else-if="buscaListaRealizada" class="text-center text--secondary pa-8">
          Nenhuma devolutiva encontrada para esse ano.
        </v-card-text>
        <v-card-text v-else class="text-center text--secondary pa-8"> Informe o ano e clique em Buscar. </v-card-text>
      </v-card>
    </v-container>

    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="4000" top>{{ snackbarText }}</v-snackbar>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import EscolaServices from "@/services/base/EscolaServices";
import InstrumentoQuestionarioServices from "@/services/rh/InstrumentoQuestionarioServices";
import AvaliacaoInstitucionalServices from "@/services/avaliacaoInstitucional/AvaliacaoInstitucionalServices";
import { salvarHandlePasta, carregarHandlePasta } from "@/libs/fileSystemStore";

export default {
  name: "DevolutivaAvaliacao",
  data: () => ({
    ano: new Date().getFullYear(),
    anoConsulta: new Date().getFullYear(),
    idInstrumento: null,
    idInstituicao: null,
    idsQuestionario: [],
    escolas: [],
    gerando: false,
    buscandoLista: false,
    buscaListaRealizada: false,
    instituicoes: [],
    arquivosPorInstituicao: {},
    baixando: {},
    excluindo: {},
    baixandoTodos: {},
    pastaSuportada: typeof window !== "undefined" && "showDirectoryPicker" in window,
    pastaNome: "",
    snackbar: false,
    snackbarText: "",
    snackbarColor: "success",
  }),
  computed: {
    ...mapGetters({
      usuarioLogado: "config/getUsuarioLogado",
      instrumentos: "avaliacao/getListaInstrumentos",
      questionarios: "avaliacao/getListaQuestionariosInstrumento",
    }),
  },
  watch: {
    ano() {
      this.idInstrumento = null;
      this.idsQuestionario = [];
      this.carregarInstrumentos();
    },
    idInstrumento(valor) {
      this.idsQuestionario = [];
      if (valor) this.carregarQuestionariosInstrumento();
    },
  },
  mounted() {
    this.carregarInstrumentos();
    this.carregarEscolas();
    this.restaurarPasta();
  },
  methods: {
    async restaurarPasta() {
      if (!this.pastaSuportada) return;
      try {
        const handle = await carregarHandlePasta();
        if (!handle) return;
        const permissao = await handle.queryPermission({ mode: "readwrite" });
        if (permissao === "granted") {
          this.pastaHandle = handle;
          this.pastaNome = handle.name;
        } else {
          this.pastaHandlePendente = handle;
          this.pastaNome = `${handle.name} (clique em "Trocar pasta" para reconectar)`;
        }
      } catch (error) {
        // Nenhuma pasta salva ainda ou permissão indisponível: segue sem pasta padrão.
      }
    },
    async selecionarPasta() {
      if (!this.pastaSuportada) {
        this.exibirMensagem("Seu navegador não suporta fixar uma pasta. Use Chrome ou Edge.", "warning");
        return;
      }
      try {
        const handle = this.pastaHandlePendente || (await window.showDirectoryPicker());
        const permissao = await handle.requestPermission({ mode: "readwrite" });
        if (permissao !== "granted") {
          this.exibirMensagem("Permissão negada para a pasta selecionada.", "error");
          return;
        }
        this.pastaHandle = handle;
        this.pastaHandlePendente = null;
        this.pastaNome = handle.name;
        await salvarHandlePasta(handle);
        this.exibirMensagem(`Pasta padrão definida: "${handle.name}".`, "success");
      } catch (error) {
        if (error?.name !== "AbortError") {
          this.exibirMensagem("Erro ao selecionar a pasta.", "error");
        }
      }
    },
    async baixarTodos(instituicao) {
      const arquivos = this.arquivosPorInstituicao[instituicao] || [];
      if (!arquivos.length) return;
      this.$set(this.baixandoTodos, instituicao, true);
      try {
        if (this.pastaSuportada && this.pastaHandle) {
          const nomePasta = instituicao.replace(/[\\/:*?"<>|]/g, "_");
          const pastaInstituicao = await this.pastaHandle.getDirectoryHandle(nomePasta, { create: true });
          for (const arquivo of arquivos) {
            const blob = await AvaliacaoInstitucionalServices.baixarDevolutiva(this.anoConsulta, instituicao, arquivo);
            const fileHandle = await pastaInstituicao.getFileHandle(arquivo, { create: true });
            const writable = await fileHandle.createWritable();
            await writable.write(blob);
            await writable.close();
          }
          this.exibirMensagem(`${arquivos.length} arquivo(s) salvos em "${this.pastaNome}/${nomePasta}".`, "success");
        } else {
          for (const arquivo of arquivos) {
            await this.baixarArquivo(instituicao, arquivo);
          }
        }
      } catch (error) {
        this.exibirMensagem("Erro ao baixar todos os arquivos.", "error");
      } finally {
        this.$set(this.baixandoTodos, instituicao, false);
      }
    },
    rotuloQuestionario(item) {
      if (!item || typeof item !== "object") return "";
      const nome = item.anexo || item.nome || item.titulo || `Questionário ${item.id}`;
      const extra = [item.instrumento, item.ano].filter(Boolean).join(" - ");
      return extra ? `${nome} (${extra})` : nome;
    },
    async carregarInstrumentos() {
      try {
        await InstrumentoQuestionarioServices.LoadLista({ anos: this.ano });
      } catch (error) {
        this.exibirMensagem("Erro ao carregar instrumentos.", "error");
      }
    },
    async carregarQuestionariosInstrumento() {
      try {
        await InstrumentoQuestionarioServices.loadQuestionariosInstrumento(this.ano, this.idInstrumento);
      } catch (error) {
        this.exibirMensagem("Erro ao carregar questionários do instrumento.", "error");
      }
    },
    async carregarEscolas() {
      try {
        const data = await EscolaServices.load({ fkadministracao: 1 });
        this.escolas = data || [];
      } catch (error) {
        this.exibirMensagem("Erro ao carregar instituições.", "error");
      }
    },
    async gerarDevolutiva() {
      this.gerando = true;
      try {
        const payload = {
          ano: this.ano,
          id_instituicao: this.idInstituicao,
          id_questionario: this.idsQuestionario,
        };
        console.log("gerarDevolutiva payload:", payload);
        const resp = await AvaliacaoInstitucionalServices.gerarDevolutiva(payload);
        if (resp?.Error) {
          this.exibirMensagem(resp.Error, "error");
          return;
        }
        this.exibirMensagem("Devolutiva gerada com sucesso.", "success");
        this.anoConsulta = this.ano;
        await this.buscarDevolutivas();
      } catch (error) {
        this.exibirMensagem(error?.response?.data?.error || "Erro ao gerar devolutiva.", "error");
      } finally {
        this.gerando = false;
      }
    },
    async buscarDevolutivas() {
      this.buscandoLista = true;
      this.buscaListaRealizada = false;
      try {
        const resp = await AvaliacaoInstitucionalServices.listarDevolutivas(this.anoConsulta);
        const devolutivas = resp?.data?.devolutivas || {};
        this.arquivosPorInstituicao = devolutivas;
        this.instituicoes = Object.keys(devolutivas);
      } catch (error) {
        this.instituicoes = [];
        this.arquivosPorInstituicao = {};
        this.exibirMensagem(error?.response?.data?.error || "Erro ao buscar devolutivas.", "error");
      } finally {
        this.buscandoLista = false;
        this.buscaListaRealizada = true;
      }
    },
    async baixarArquivo(instituicao, arquivo) {
      const chave = `${instituicao}/${arquivo}`;
      this.$set(this.baixando, chave, true);
      try {
        const blob = await AvaliacaoInstitucionalServices.baixarDevolutiva(this.anoConsulta, instituicao, arquivo);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = arquivo;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        this.exibirMensagem("Erro ao baixar arquivo.", "error");
      } finally {
        this.$set(this.baixando, chave, false);
      }
    },
    async excluirArquivo(instituicao, arquivo) {
      if (!confirm(`Deseja excluir o arquivo "${arquivo}" de ${instituicao}?`)) return;
      const chave = `${instituicao}/${arquivo}`;
      this.$set(this.excluindo, chave, true);
      try {
        await AvaliacaoInstitucionalServices.removerDevolutiva(this.anoConsulta, instituicao, arquivo);
        this.arquivosPorInstituicao[instituicao] = (this.arquivosPorInstituicao[instituicao] || []).filter(
          (a) => a !== arquivo
        );
        if (!this.arquivosPorInstituicao[instituicao].length) {
          this.instituicoes = this.instituicoes.filter((i) => i !== instituicao);
        }
        this.exibirMensagem("Arquivo excluído com sucesso.", "success");
      } catch (error) {
        this.exibirMensagem(error?.response?.data?.error || "Erro ao excluir arquivo.", "error");
      } finally {
        this.$set(this.excluindo, chave, false);
      }
    },
    exibirMensagem(texto, cor) {
      this.snackbarText = texto;
      this.snackbarColor = cor;
      this.snackbar = true;
    },
  },
};
</script>

<style scoped>
.devolutiva-container {
  padding: 16px;
}

.questionarios-lista {
  max-height: 160px;
  overflow-y: auto;
  padding-top: 4px;
  padding-bottom: 4px;
}

@media (min-width: 1264px) {
  .devolutiva-container {
    padding-left: 40px;
    padding-right: 40px;
  }
}
</style>